import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { authHeaders } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

function BlueprintCard({ bp, onSelect, selected }) {
  const typeIcons = { pdf: 'PDF', dxf: 'DXF', dwg: 'DWG', png: 'IMG', jpg: 'IMG', jpeg: 'IMG', svg: 'SVG' };
  const typeColors = { pdf: 'bg-red-600', dxf: 'bg-blue-600', dwg: 'bg-purple-600', png: 'bg-green-600', jpg: 'bg-green-600', svg: 'bg-cyan-600' };
  return (
    <div data-testid={`bp-${bp.id}`} onClick={() => onSelect(bp)}
      className={`bg-slate-800/60 border rounded-lg p-4 cursor-pointer hover:bg-slate-700/60 transition ${selected?.id === bp.id ? 'border-blue-500 bg-slate-700/80' : 'border-slate-700'}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 ${typeColors[bp.file_type] || 'bg-slate-600'} rounded-lg flex items-center justify-center text-white text-xs font-bold`}>
          {typeIcons[bp.file_type] || bp.file_type?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium truncate">{bp.name}</p>
          <p className="text-slate-400 text-xs">{bp.description || bp.original_filename} | {(bp.file_size / 1024).toFixed(0)}KB</p>
          {bp.layers?.length > 0 && <p className="text-cyan-400 text-xs mt-0.5">{bp.layers.length} capas</p>}
          <div className="flex gap-1 mt-1 flex-wrap">
            {bp.ai_analysis && <span className="text-xs bg-green-900/40 text-green-300 px-2 py-0.5 rounded">IA</span>}
            {bp.schedule && <span className="text-xs bg-amber-900/40 text-amber-300 px-2 py-0.5 rounded">Cronograma</span>}
            {bp.bom && <span className="text-xs bg-blue-900/40 text-blue-300 px-2 py-0.5 rounded">BOM</span>}
          </div>
        </div>
        <p className="text-slate-500 text-xs">{new Date(bp.created_at).toLocaleDateString('es-ES')}</p>
      </div>
    </div>
  );
}

function PdfViewer({ url }) {
  return (
    <div data-testid="pdf-viewer" className="w-full h-[600px] bg-slate-900 rounded-lg overflow-hidden">
      <iframe src={url} className="w-full h-full" title="PDF Viewer" />
    </div>
  );
}

function SvgViewer({ url }) {
  const [scale, setScale] = useState(1);
  return (
    <div data-testid="svg-viewer" className="bg-white rounded-lg overflow-auto relative" style={{ maxHeight: 600 }}>
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur p-2 flex gap-2 border-b">
        <Button size="sm" variant="outline" onClick={() => setScale(s => Math.max(0.2, s - 0.2))}>-</Button>
        <span className="text-sm text-slate-600 px-2 py-1">{Math.round(scale * 100)}%</span>
        <Button size="sm" variant="outline" onClick={() => setScale(s => Math.min(5, s + 0.2))}>+</Button>
        <Button size="sm" variant="outline" onClick={() => setScale(1)}>Reset</Button>
      </div>
      <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left', transition: 'transform 0.2s' }}>
        <img src={url} alt="Blueprint SVG" className="max-w-none" />
      </div>
    </div>
  );
}

function ImageViewer({ url }) {
  const [scale, setScale] = useState(1);
  return (
    <div data-testid="image-viewer" className="bg-slate-900 rounded-lg overflow-auto" style={{ maxHeight: 600 }}>
      <div className="sticky top-0 z-10 bg-slate-800/90 backdrop-blur p-2 flex gap-2 border-b border-slate-700">
        <Button size="sm" variant="outline" onClick={() => setScale(s => Math.max(0.2, s - 0.2))}>-</Button>
        <span className="text-sm text-slate-300 px-2 py-1">{Math.round(scale * 100)}%</span>
        <Button size="sm" variant="outline" onClick={() => setScale(s => Math.min(5, s + 0.2))}>+</Button>
      </div>
      <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}>
        <img src={url} alt="Blueprint" className="max-w-none" />
      </div>
    </div>
  );
}

function AnnotationPanel({ blueprint, onAdd, onDelete }) {
  const [text, setText] = useState('');
  const annotations = blueprint?.annotations || [];
  const measurements = blueprint?.measurements || [];

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-white font-semibold text-sm mb-2">Anotaciones ({annotations.length})</h4>
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {annotations.map(a => (
            <div key={a.id} className="bg-slate-800 border border-slate-700 rounded p-2 flex justify-between items-start">
              <div>
                <p className="text-white text-xs">{a.text}</p>
                <p className="text-slate-500 text-[10px]">{a.created_by} | {new Date(a.created_at).toLocaleDateString('es-ES')}</p>
              </div>
              <button onClick={() => onDelete(a.id)} className="text-red-400 text-xs hover:text-red-300">X</button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-2">
          <input className="flex-1 bg-slate-900 text-white border border-slate-600 rounded px-2 py-1.5 text-xs"
            placeholder="Agregar nota..." value={text} onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && text.trim()) { onAdd(text); setText(''); }}} />
          <Button size="sm" onClick={() => { if (text.trim()) { onAdd(text); setText(''); }}} className="text-xs">+</Button>
        </div>
      </div>
      {measurements.length > 0 && (
        <div>
          <h4 className="text-white font-semibold text-sm mb-2">Medidas ({measurements.length})</h4>
          {measurements.map(m => (
            <div key={m.id} className="bg-slate-800 border border-slate-700 rounded p-2 text-xs text-white">
              {m.label}: {m.value} {m.unit}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AnalysisPanel({ analysis }) {
  if (!analysis) return <p className="text-slate-500 text-sm">Sin analisis IA. Haga click en "Analizar con IA".</p>;
  return (
    <div className="space-y-3">
      {analysis.plan_type && (
        <div className="bg-blue-900/30 border border-blue-800 rounded p-3">
          <p className="text-blue-300 text-xs font-semibold">TIPO DE PLANO</p>
          <p className="text-white text-sm">{analysis.plan_type}</p>
        </div>
      )}
      {analysis.areas && (
        <div className="bg-slate-800 border border-slate-700 rounded p-3">
          <p className="text-slate-400 text-xs font-semibold mb-1">AREAS</p>
          {(Array.isArray(analysis.areas) ? analysis.areas : [analysis.areas]).map((a, i) => (
            <p key={i} className="text-white text-xs">{typeof a === 'string' ? a : JSON.stringify(a)}</p>
          ))}
        </div>
      )}
      {analysis.estimated_quantities && (
        <div className="bg-green-900/20 border border-green-800 rounded p-3">
          <p className="text-green-400 text-xs font-semibold mb-1">CANTIDADES ESTIMADAS</p>
          {typeof analysis.estimated_quantities === 'object' && Object.entries(analysis.estimated_quantities).map(([k, v]) => (
            <p key={k} className="text-white text-xs">{k}: <span className="text-green-300 font-semibold">{v}</span></p>
          ))}
        </div>
      )}
      {analysis.material_suggestions && (
        <div className="bg-yellow-900/20 border border-yellow-800 rounded p-3">
          <p className="text-yellow-400 text-xs font-semibold mb-1">MATERIALES SUGERIDOS</p>
          {(Array.isArray(analysis.material_suggestions) ? analysis.material_suggestions : [analysis.material_suggestions]).map((m, i) => (
            <p key={i} className="text-white text-xs">{typeof m === 'string' ? m : JSON.stringify(m)}</p>
          ))}
        </div>
      )}
      {analysis.observations && (
        <div className="bg-red-900/20 border border-red-800 rounded p-3">
          <p className="text-red-400 text-xs font-semibold mb-1">OBSERVACIONES</p>
          {(Array.isArray(analysis.observations) ? analysis.observations : [analysis.observations]).map((o, i) => (
            <p key={i} className="text-white text-xs">{typeof o === 'string' ? o : JSON.stringify(o)}</p>
          ))}
        </div>
      )}
      {analysis.notes && (
        <div className="bg-slate-800 border border-slate-700 rounded p-3">
          <p className="text-slate-400 text-xs font-semibold mb-1">NOTAS DEL PLANO</p>
          {(Array.isArray(analysis.notes) ? analysis.notes : [analysis.notes]).map((n, i) => (
            <p key={i} className="text-white text-xs">{typeof n === 'string' ? n : JSON.stringify(n)}</p>
          ))}
        </div>
      )}
    </div>
  );
}

/* ==================== SCHEDULE PANEL ==================== */
function SchedulePanel({ schedule, generating, onGenerate }) {
  if (generating) {
    return (
      <div data-testid="schedule-loading" className="flex flex-col items-center justify-center py-16 gap-3">
        <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-amber-300 text-sm">Generando cronograma con IA...</p>
        <p className="text-slate-500 text-xs">Analizando plano y estimando fases de trabajo</p>
      </div>
    );
  }

  if (!schedule) {
    return (
      <div data-testid="schedule-empty" className="text-center py-12">
        <div className="w-16 h-16 mx-auto bg-amber-900/30 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
        </div>
        <p className="text-slate-400 text-sm mb-3">Sin cronograma generado</p>
        <Button data-testid="generate-schedule-btn" onClick={onGenerate} className="bg-amber-600 hover:bg-amber-700 text-white">
          Generar Cronograma con IA
        </Button>
      </div>
    );
  }

  if (schedule.error) {
    return <div className="bg-red-900/30 border border-red-700 p-4 rounded text-red-300 text-sm">{schedule.raw || schedule.error}</div>;
  }

  const phases = schedule.phases || [];
  let dayCounter = 1;

  return (
    <div data-testid="schedule-panel" className="space-y-4">
      {/* Summary header */}
      <div className="bg-amber-900/20 border border-amber-800/60 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-amber-300 font-bold text-sm">CRONOGRAMA DE ACTIVIDADES</h4>
          <div className="flex items-center gap-3">
            <span className="text-white text-sm font-semibold bg-amber-700/50 px-3 py-1 rounded-full">
              {schedule.estimated_total_days || '?'} dias estimados
            </span>
            <Button data-testid="regenerate-schedule-btn" size="sm" variant="outline" onClick={onGenerate} className="text-xs border-amber-600 text-amber-300 hover:bg-amber-900/40">
              Regenerar
            </Button>
          </div>
        </div>
        {schedule.project_summary && <p className="text-slate-300 text-xs">{schedule.project_summary}</p>}
      </div>

      {/* Phases timeline */}
      {phases.map((phase, pi) => {
        const phaseStart = dayCounter;
        const phaseDays = (phase.tasks || []).reduce((sum, t) => sum + (t.duration_days || 0), 0);
        return (
          <div key={pi} data-testid={`phase-${pi}`} className="bg-slate-800/60 border border-slate-700 rounded-lg overflow-hidden">
            {/* Phase header */}
            <div className="bg-slate-700/60 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                  {phase.phase_number || pi + 1}
                </span>
                <div>
                  <p className="text-white font-semibold text-sm">{phase.phase}</p>
                  <p className="text-slate-400 text-xs">Dia {phaseStart} - {phaseStart + phaseDays - 1} | {phaseDays} dias</p>
                </div>
              </div>
            </div>
            {/* Tasks table */}
            <div className="divide-y divide-slate-700/50">
              {(phase.tasks || []).map((task, ti) => {
                const taskStart = dayCounter;
                dayCounter += task.duration_days || 0;
                const priorityColors = { alta: 'bg-red-600', media: 'bg-yellow-600', baja: 'bg-green-600' };
                return (
                  <div key={ti} className="px-4 py-3 flex items-center gap-4 hover:bg-slate-700/30 transition">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs font-medium">{task.task}</p>
                      {task.depends_on && <p className="text-slate-500 text-[10px] mt-0.5">Depende de: {task.depends_on}</p>}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className={`text-[10px] px-2 py-0.5 rounded text-white ${priorityColors[task.priority] || 'bg-slate-600'}`}>
                        {task.priority || 'media'}
                      </span>
                      <span className="text-slate-400 text-xs whitespace-nowrap">{task.duration_days}d</span>
                      <span className="text-slate-500 text-[10px] whitespace-nowrap">D{taskStart}-{taskStart + (task.duration_days || 1) - 1}</span>
                      {task.resources && <span className="text-cyan-400 text-[10px] truncate max-w-[120px]">{task.resources}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ==================== BOM PANEL ==================== */
function BomPanel({ bom, generating, onGenerate, onCreateQuote }) {
  if (generating) {
    return (
      <div data-testid="bom-loading" className="flex flex-col items-center justify-center py-16 gap-3">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-blue-300 text-sm">Generando Bill of Materials con IA...</p>
        <p className="text-slate-500 text-xs">Analizando plano y cruzando con catalogo de precios</p>
      </div>
    );
  }

  if (!bom) {
    return (
      <div data-testid="bom-empty" className="text-center py-12">
        <div className="w-16 h-16 mx-auto bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>
        </div>
        <p className="text-slate-400 text-sm mb-3">Sin listado de materiales generado</p>
        <Button data-testid="generate-bom-btn" onClick={onGenerate} className="bg-blue-600 hover:bg-blue-700 text-white">
          Generar BOM con IA
        </Button>
      </div>
    );
  }

  if (bom.error) {
    return <div className="bg-red-900/30 border border-red-700 p-4 rounded text-red-300 text-sm">{bom.raw || bom.error}</div>;
  }

  const items = bom.items || [];
  const categories = [...new Set(items.map(i => i.category))];

  return (
    <div data-testid="bom-panel" className="space-y-4">
      {/* Summary header */}
      <div className="bg-blue-900/20 border border-blue-800/60 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-blue-300 font-bold text-sm">BILL OF MATERIALS (BOM)</h4>
          <div className="flex items-center gap-2">
            <Button data-testid="regenerate-bom-btn" size="sm" variant="outline" onClick={onGenerate} className="text-xs border-blue-600 text-blue-300 hover:bg-blue-900/40">
              Regenerar
            </Button>
            <Button data-testid="bom-to-quote-btn" size="sm" onClick={onCreateQuote} className="text-xs bg-green-600 hover:bg-green-700 text-white">
              Crear Cotizacion
            </Button>
          </div>
        </div>
        {bom.project_description && <p className="text-slate-300 text-xs">{bom.project_description}</p>}
      </div>

      {/* Totals bar */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 text-center">
          <p className="text-slate-400 text-[10px] uppercase">Materiales</p>
          <p className="text-white font-bold text-lg">${(bom.subtotal_materials || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 text-center">
          <p className="text-slate-400 text-[10px] uppercase">Labor</p>
          <p className="text-white font-bold text-lg">${(bom.subtotal_labor || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
        </div>
        <div className="bg-green-900/30 border border-green-700 rounded-lg p-3 text-center">
          <p className="text-green-400 text-[10px] uppercase">Total</p>
          <p className="text-green-300 font-bold text-lg">${(bom.grand_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
        </div>
      </div>

      {/* Items by category */}
      {categories.map(cat => {
        const catItems = items.filter(i => i.category === cat);
        const catTotal = catItems.reduce((s, i) => s + (i.line_total || 0), 0);
        return (
          <div key={cat} className="bg-slate-800/60 border border-slate-700 rounded-lg overflow-hidden">
            <div className="bg-slate-700/60 px-4 py-2 flex justify-between items-center">
              <p className="text-white font-semibold text-sm">{cat}</p>
              <p className="text-slate-300 text-xs font-medium">${catTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            </div>
            <table className="w-full">
              <thead>
                <tr className="text-slate-400 text-[10px] uppercase border-b border-slate-700/50">
                  <th className="text-left px-4 py-2">Item</th>
                  <th className="text-center px-2 py-2">Cant.</th>
                  <th className="text-center px-2 py-2">Unidad</th>
                  <th className="text-right px-2 py-2">Material</th>
                  <th className="text-right px-2 py-2">Labor</th>
                  <th className="text-right px-4 py-2">Total Linea</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/30">
                {catItems.map((item, idx) => (
                  <tr key={idx} className="hover:bg-slate-700/20 transition">
                    <td className="px-4 py-2 text-white text-xs">
                      {item.item}
                      {item.fuera_de_catalogo && <span className="ml-2 text-[9px] bg-orange-800/50 text-orange-300 px-1.5 rounded">Fuera de catalogo</span>}
                      {item.notes && <p className="text-slate-500 text-[10px] mt-0.5">{item.notes}</p>}
                    </td>
                    <td className="text-center text-white text-xs font-semibold px-2 py-2">{item.quantity}</td>
                    <td className="text-center text-slate-400 text-xs px-2 py-2">{item.unit}</td>
                    <td className="text-right text-slate-300 text-xs px-2 py-2">${(item.unit_price || 0).toFixed(2)}</td>
                    <td className="text-right text-slate-300 text-xs px-2 py-2">${(item.labor_price || 0).toFixed(2)}</td>
                    <td className="text-right text-white text-xs font-semibold px-4 py-2">${(item.line_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      {/* Notes */}
      {bom.notes && (
        <div className="bg-slate-800/60 border border-slate-600 rounded-lg p-3">
          <p className="text-slate-400 text-xs font-semibold mb-1">NOTAS DEL ESTIMADO</p>
          <p className="text-slate-300 text-xs">{bom.notes}</p>
        </div>
      )}
    </div>
  );
}

/* ==================== MAIN PAGE ==================== */
export default function BlueprintPage() {
  const [blueprints, setBlueprints] = useState([]);
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [generatingSchedule, setGeneratingSchedule] = useState(false);
  const [generatingBom, setGeneratingBom] = useState(false);
  const [generatingBudget, setGeneratingBudget] = useState(false);
  const [budget, setBudget] = useState(null);
  const [msg, setMsg] = useState('');
  const [activeTab, setActiveTab] = useState('viewer');
  const [projectFilter, setProjectFilter] = useState('');
  const fileRef = useRef();

  const fetchBlueprints = useCallback(async () => {
    try {
      const params = projectFilter ? `?project_id=${projectFilter}` : '';
      const res = await fetch(`${API}/api/blueprints${params}`, { credentials: 'include' });
      if (res.ok) setBlueprints(await res.json());
    } catch (e) { console.error(e); }
  }, [projectFilter]);

  const fetchDetail = async (id) => {
    const res = await fetch(`${API}/api/blueprints/${id}`, { credentials: 'include' });
    if (res.ok) setSelected(await res.json());
  };

  useEffect(() => { fetchBlueprints(); }, [fetchBlueprints]);

  const showMsg = (t) => { setMsg(t); setTimeout(() => setMsg(''), 4000); };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const CHUNK_SIZE = 512 * 1024; // 512KB per chunk to bypass strict proxy limits
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const uploadId = Date.now().toString(36) + Math.random().toString(36).slice(2);

      if (totalChunks <= 1) {
        // Small file - direct upload
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', file.name);
        if (projectFilter) formData.append('project_id', projectFilter);
        const res = await fetch(`${API}/api/blueprints/upload`, {
          method: 'POST', credentials: 'include', body: formData, headers: authHeaders()
        });
        if (res.status === 401) {
          showMsg('Sesion expirada. Cierra sesion y vuelve a iniciar.');
          setTimeout(() => { window.location.href = '/login'; }, 2500);
          setUploading(false); return;
        }
        if (res.ok) {
          const data = await res.json();
          showMsg(`"${data.name}" subido OK`);
          fetchBlueprints(); fetchDetail(data.id);
        } else {
          const err = await res.json().catch(() => ({}));
          showMsg(err.detail || 'Error al subir');
        }
      } else {
        // Large file - chunked upload to bypass proxy size limits
        showMsg(`Subiendo ${(file.size/1024/1024).toFixed(1)}MB en ${totalChunks} partes...`);
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const chunk = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
          const fd = new FormData();
          fd.append('chunk', chunk, `chunk_${i}`);
          fd.append('upload_id', uploadId);
          fd.append('chunk_index', i.toString());
          fd.append('total_chunks', totalChunks.toString());
          fd.append('filename', file.name);
          const res = await fetch(`${API}/api/blueprints/upload-chunk`, { method: 'POST', credentials: 'include', body: fd, headers: authHeaders() });
          if (res.status === 401) {
            showMsg('Sesion expirada. Cierra sesion y vuelve a iniciar.');
            setTimeout(() => { window.location.href = '/login'; }, 2500);
            setUploading(false); return;
          }
          if (!res.ok) {
            const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
            showMsg(`Error parte ${i+1}/${totalChunks}: ${err.detail || res.statusText}`);
            setUploading(false); return;
          }
        }
        const res = await fetch(`${API}/api/blueprints/upload-complete`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ upload_id: uploadId, filename: file.name, name: file.name, total_chunks: totalChunks, project_id: projectFilter })
        });
        if (res.status === 401) {
          showMsg('Sesion expirada. Cierra sesion y vuelve a iniciar.');
          setTimeout(() => { window.location.href = '/login'; }, 2500);
          setUploading(false); return;
        }
        if (res.ok) {
          const data = await res.json();
          showMsg(`"${data.name}" subido (${(file.size/1024/1024).toFixed(1)}MB)`);
          fetchBlueprints(); fetchDetail(data.id);
        } else {
          const err = await res.json().catch(() => ({}));
          showMsg(err.detail || 'Error al completar subida');
        }
      }
    } catch (e) { showMsg('Error al subir archivo'); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleAnalyze = async () => {
    if (!selected) return;
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/api/blueprints/${selected.id}/analyze`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        showMsg('Analisis IA completado');
        fetchDetail(selected.id);
      } else {
        const err = await res.json();
        showMsg(err.detail || 'Error al analizar');
      }
    } catch (e) { showMsg('Error'); }
    setAnalyzing(false);
  };

  const handleGenerateSchedule = async () => {
    if (!selected) return;
    setGeneratingSchedule(true);
    try {
      const res = await fetch(`${API}/api/blueprints/${selected.id}/generate-schedule`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        showMsg('Cronograma generado exitosamente');
        fetchDetail(selected.id);
        fetchBlueprints();
      } else {
        const err = await res.json();
        showMsg(err.detail || 'Error al generar cronograma');
      }
    } catch (e) { showMsg('Error de conexion'); }
    setGeneratingSchedule(false);
  };

  const handleGenerateBom = async () => {
    if (!selected) return;
    setGeneratingBom(true);
    try {
      const res = await fetch(`${API}/api/blueprints/${selected.id}/generate-bom`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        showMsg('BOM generado exitosamente');
        fetchDetail(selected.id);
        fetchBlueprints();
      } else {
        const err = await res.json();
        showMsg(err.detail || 'Error al generar BOM');
      }
    } catch (e) { showMsg('Error de conexion'); }
    setGeneratingBom(false);
  };

  const handleBomToQuote = async () => {
    if (!selected?.bom) return;
    try {
      const res = await fetch(`${API}/api/blueprints/${selected.id}/bom-to-quote`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (res.ok) {
        const data = await res.json();
        showMsg(`Cotizacion creada: $${data.total?.toLocaleString('en-US', { minimumFractionDigits: 2 })} (ID: ${data.quote_id?.slice(0, 8)}...)`);
      } else {
        const err = await res.json();
        showMsg(err.detail || 'Error al crear cotizacion');
      }
    } catch (e) { showMsg('Error de conexion'); }
  };

  const handleGenerateBudget = async () => {
    if (!selected?.bom) { showMsg('Genere primero el BOM'); return; }
    setGeneratingBudget(true);
    try {
      const res = await fetch(`${API}/api/project-budget/generate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ blueprint_id: selected.id, project_name: selected.name, overhead_pct: 15, margin_pct: 25, contingency_pct: 10 })
      });
      if (res.ok) {
        const data = await res.json();
        setBudget(data);
        showMsg(`Presupuesto generado: $${data.grand_total?.toLocaleString('en-US', { minimumFractionDigits: 2 })}`);
      } else {
        const err = await res.json();
        showMsg(err.detail || 'Error al generar presupuesto');
      }
    } catch (e) { showMsg('Error de conexion'); }
    setGeneratingBudget(false);
  };

  const handleAddAnnotation = async (text) => {
    if (!selected) return;
    await fetch(`${API}/api/blueprints/${selected.id}/annotations`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, type: 'note' })
    });
    fetchDetail(selected.id);
  };

  const handleDeleteAnnotation = async (annotId) => {
    if (!selected) return;
    await fetch(`${API}/api/blueprints/${selected.id}/annotations/${annotId}`, {
      method: 'DELETE', credentials: 'include'
    });
    fetchDetail(selected.id);
  };

  const handleDelete = async () => {
    if (!selected || !window.confirm('Eliminar este plano?')) return;
    await fetch(`${API}/api/blueprints/${selected.id}`, { method: 'DELETE', credentials: 'include' });
    setSelected(null);
    fetchBlueprints();
  };

  const getViewerUrl = (bp) => {
    if (!bp) return '';
    if (bp.has_svg) return `${API}/api/blueprints/${bp.id}/svg`;
    return `${API}/api/blueprints/${bp.id}/file`;
  };

  const renderViewer = () => {
    if (!selected) return <p className="text-slate-500 text-center py-20">Seleccione un plano de la lista</p>;
    const url = getViewerUrl(selected);
    const type = selected.file_type;
    if (type === 'pdf') return <PdfViewer url={url} />;
    if (type === 'dxf' && selected.has_svg) return <SvgViewer url={url} />;
    if (['png', 'jpg', 'jpeg', 'svg'].includes(type)) return <ImageViewer url={url} />;
    if (type === 'dxf' && !selected.has_svg) return <p className="text-yellow-400 text-center py-10">DXF sin conversion SVG. Descargue el archivo para ver en AutoCAD.</p>;
    if (type === 'dwg') return <p className="text-yellow-400 text-center py-10">Archivos DWG requieren AutoCAD. Descargue el archivo o suba version DXF/PDF.</p>;
    return <p className="text-slate-500 text-center py-10">Formato no soportado para vista previa</p>;
  };

  return (
    <div data-testid="blueprint-page" className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Visor de Planos</h1>
          <p className="text-slate-400 text-sm">{blueprints.length} archivos | PDF, DXF, DWG, Imagenes, Video, Documentos (hasta 100MB) | Cronograma + BOM con IA</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".pdf,.dxf,.dwg,.png,.jpg,.jpeg,.svg,.tiff,.bmp,.mp4,.mov,.avi,.wmv,.mkv,.webm,.doc,.docx,.xls,.xlsx,.pptx,.csv,.txt,.zip" onChange={handleUpload} className="hidden" id="bp-upload" />
          <Button data-testid="upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? 'Subiendo...' : 'Subir Plano'}
          </Button>
          {selected && (
            <>
              <Button data-testid="analyze-btn" variant="outline" onClick={handleAnalyze} disabled={analyzing}>
                {analyzing ? 'Analizando...' : 'Analizar con IA'}
              </Button>
              <a href={`${API}/api/blueprints/${selected.id}/file`} download>
                <Button variant="outline">Descargar</Button>
              </a>
              <Button variant="outline" className="text-red-400 border-red-600" onClick={handleDelete}>Eliminar</Button>
            </>
          )}
        </div>
      </div>

      {msg && <div className="bg-blue-900/30 border border-blue-700 text-blue-200 px-4 py-2 rounded text-sm">{msg}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left: Blueprint list */}
        <div className="lg:col-span-1 space-y-2 max-h-[700px] overflow-y-auto">
          {blueprints.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-8">Sin planos. Suba un archivo PDF, DXF o imagen.</p>
          ) : (
            blueprints.map(bp => <BlueprintCard key={bp.id} bp={bp} onSelect={(b) => { setSelected(null); fetchDetail(b.id); }} selected={selected} />)
          )}
        </div>

        {/* Right: Viewer + Tabs */}
        <div className="lg:col-span-3">
          {selected && (
            <div className="mb-3">
              <h2 className="text-white font-bold text-lg">{selected.name}</h2>
              <p className="text-slate-400 text-xs">{selected.file_type?.toUpperCase()} | {selected.original_filename} | {selected.created_by} | {new Date(selected.created_at).toLocaleDateString('es-ES')}</p>
            </div>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="flex-wrap">
              <TabsTrigger value="viewer">Visor</TabsTrigger>
              <TabsTrigger value="schedule" data-testid="tab-schedule">
                Cronograma {selected?.schedule ? '(*)' : ''}
              </TabsTrigger>
              <TabsTrigger value="bom" data-testid="tab-bom">
                BOM {selected?.bom ? '(*)' : ''}
              </TabsTrigger>
              <TabsTrigger value="budget" data-testid="tab-budget">
                Presupuesto {budget ? '(*)' : ''}
              </TabsTrigger>
              <TabsTrigger value="annotations">Anotaciones</TabsTrigger>
              <TabsTrigger value="analysis">Analisis IA</TabsTrigger>
              {selected?.layers?.length > 0 && <TabsTrigger value="layers">Capas ({selected.layers.length})</TabsTrigger>}
            </TabsList>

            <TabsContent value="viewer" className="mt-3">
              {renderViewer()}
            </TabsContent>

            <TabsContent value="schedule" className="mt-3">
              <SchedulePanel
                schedule={selected?.schedule}
                generating={generatingSchedule}
                onGenerate={handleGenerateSchedule}
              />
            </TabsContent>

            <TabsContent value="bom" className="mt-3">
              <BomPanel
                bom={selected?.bom}
                generating={generatingBom}
                onGenerate={handleGenerateBom}
                onCreateQuote={handleBomToQuote}
              />
            </TabsContent>

            <TabsContent value="budget" className="mt-3">
              {!selected?.bom ? (
                <div className="text-center py-12">
                  <p className="text-slate-400 text-sm mb-2">Genere primero el BOM para crear el presupuesto</p>
                  <Button variant="outline" onClick={() => setActiveTab('bom')}>Ir a BOM</Button>
                </div>
              ) : !budget && !generatingBudget ? (
                <div data-testid="budget-empty" className="text-center py-12">
                  <div className="w-16 h-16 mx-auto bg-emerald-900/30 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <p className="text-slate-400 text-sm mb-3">BOM disponible. Genere el presupuesto completo del proyecto.</p>
                  <Button data-testid="generate-budget-btn" onClick={handleGenerateBudget} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    Generar Presupuesto Completo
                  </Button>
                </div>
              ) : generatingBudget ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-emerald-300 text-sm">Generando presupuesto con IA...</p>
                </div>
              ) : budget ? (
                <div data-testid="budget-panel" className="space-y-4">
                  <div className="bg-emerald-900/20 border border-emerald-800/60 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-emerald-300 font-bold text-sm">PRESUPUESTO: {budget.project_name}</h4>
                      <Button data-testid="regenerate-budget-btn" size="sm" variant="outline" onClick={handleGenerateBudget} className="text-xs border-emerald-600 text-emerald-300">Regenerar</Button>
                    </div>
                  </div>

                  {/* Cost breakdown */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 text-center">
                      <p className="text-slate-400 text-[10px] uppercase">Materiales</p>
                      <p className="text-white font-bold text-lg">${(budget.subtotal_materials || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                    </div>
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 text-center">
                      <p className="text-slate-400 text-[10px] uppercase">Labor</p>
                      <p className="text-white font-bold text-lg">${(budget.subtotal_labor || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                    </div>
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 text-center">
                      <p className="text-slate-400 text-[10px] uppercase">Costo Directo</p>
                      <p className="text-white font-bold text-lg">${(budget.subtotal_direct || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                    </div>
                    <div className="bg-emerald-900/30 border border-emerald-700 rounded-lg p-3 text-center">
                      <p className="text-emerald-400 text-[10px] uppercase">Total Proyecto</p>
                      <p className="text-emerald-300 font-bold text-xl">${(budget.grand_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                    </div>
                  </div>

                  {/* Margins breakdown */}
                  <div className="bg-slate-800/60 border border-slate-700 rounded-lg overflow-hidden">
                    <div className="bg-slate-700/60 px-4 py-2"><p className="text-white font-semibold text-sm">Desglose de Costos</p></div>
                    <div className="divide-y divide-slate-700/50">
                      {[
                        { label: 'Costo Directo (Materiales + Labor)', val: budget.subtotal_direct },
                        { label: `Overhead (${budget.overhead_pct}%)`, val: budget.overhead_amount, accent: true },
                        { label: `Margen de Ganancia (${budget.margin_pct}%)`, val: budget.margin_amount, accent: true },
                        { label: `Contingencia (${budget.contingency_pct}%)`, val: budget.contingency_amount, accent: true },
                        { label: 'TOTAL PRESUPUESTO', val: budget.grand_total, bold: true },
                      ].map((row, idx) => (
                        <div key={idx} className={`px-4 py-2 flex justify-between items-center ${row.bold ? 'bg-emerald-900/20' : ''}`}>
                          <span className={`text-xs ${row.bold ? 'text-emerald-300 font-bold' : row.accent ? 'text-amber-300' : 'text-slate-300'}`}>{row.label}</span>
                          <span className={`text-xs font-semibold ${row.bold ? 'text-emerald-300 text-base' : 'text-white'}`}>${(row.val || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* By category */}
                  {budget.price_per_category && Object.keys(budget.price_per_category).length > 0 && (
                    <div className="bg-slate-800/60 border border-slate-700 rounded-lg overflow-hidden">
                      <div className="bg-slate-700/60 px-4 py-2"><p className="text-white font-semibold text-sm">Costo por Categoria</p></div>
                      <table className="w-full">
                        <thead><tr className="text-slate-400 text-[10px] uppercase border-b border-slate-700/50">
                          <th className="text-left px-4 py-2">Categoria</th>
                          <th className="text-right px-2 py-2">Material</th>
                          <th className="text-right px-2 py-2">Labor</th>
                          <th className="text-right px-4 py-2">Total</th>
                        </tr></thead>
                        <tbody className="divide-y divide-slate-700/30">
                          {Object.entries(budget.price_per_category).map(([cat, vals]) => (
                            <tr key={cat}><td className="px-4 py-2 text-white text-xs">{cat}</td>
                              <td className="text-right px-2 py-2 text-slate-300 text-xs">${(vals.materials || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                              <td className="text-right px-2 py-2 text-slate-300 text-xs">${(vals.labor || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                              <td className="text-right px-4 py-2 text-white text-xs font-semibold">${(vals.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* AI Recommendations */}
                  {budget.ai_recommendations && (
                    <div className="bg-indigo-900/20 border border-indigo-800 rounded-lg p-4">
                      <p className="text-indigo-300 text-xs font-semibold mb-2">RECOMENDACIONES IA</p>
                      <p className="text-slate-300 text-xs whitespace-pre-line">{budget.ai_recommendations}</p>
                    </div>
                  )}
                </div>
              ) : null}
            </TabsContent>

            <TabsContent value="annotations" className="mt-3">
              <AnnotationPanel blueprint={selected} onAdd={handleAddAnnotation} onDelete={handleDeleteAnnotation} />
            </TabsContent>

            <TabsContent value="analysis" className="mt-3">
              <AnalysisPanel analysis={selected?.ai_analysis} />
            </TabsContent>

            {selected?.layers?.length > 0 && (
              <TabsContent value="layers" className="mt-3">
                <div className="space-y-1">
                  {selected.layers.map((l, i) => (
                    <div key={i} className="bg-slate-800 border border-slate-700 rounded p-2 flex items-center gap-2">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: `hsl(${(l.color || 0) * 25}, 70%, 50%)` }} />
                      <span className="text-white text-sm">{l.name}</span>
                    </div>
                  ))}
                </div>
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
    </div>
  );
}
